/**
 * Beneficiary contract, shared by `api/recon` (invoice beneficiaries) and
 * `api/portfolio` (invoice-template payment rules).
 *
 * Field optionality is taken from the generated Lagom contract
 * (`ConsolidatedWalletBeneficiary`, `PartyBeneficiary`, `EasyPayBeneficiary` and
 * `DepositBeneficiary` are all derived from the case class, so they are trustworthy).
 * The wire uses the wrapper encoding `{ type, value }`, which is why every type below
 * is the *rule* (wrapper) rather than the bare value.
 *
 * @link https://github.com/CastleOne/phoenix/blob/4d217b6b01b655dca470f8377a9e65a2246a4c2a/party-api/src/main/scala/com/phoenix/party/api/PartyType.scala
 */

/**
 * A const object rather than a string `enum`: the enum member type is not assignable from
 * a bare string literal, which forced every fixture and API payload that writes
 * `type: 'PartyBeneficiary'` to import the enum. This keeps `BeneficiaryType.X` working as
 * a value while the discriminants stay plain string literal types.
 */
export const BeneficiaryType = {
  PartyBeneficiary: 'PartyBeneficiary',
  EasyPayBeneficiary: 'EasyPayBeneficiary',
  DepositBeneficiary: 'DepositBeneficiary',
  ConsolidatedWalletBeneficiary: 'ConsolidatedWalletBeneficiary',
} as const

export type BeneficiaryType = typeof BeneficiaryType[keyof typeof BeneficiaryType]

export type PartyBeneficiaryValue = {
  id?: string
  partyId: string
  partyTag: string
  reference: string
  amount?: number
  vat?: boolean
  /**
   * Neither field is on the generated contract, but both come back on real responses and
   * are validated by `AdhocInvoiceForm`'s beneficiary schema.
   */
  beneficiaryCategory?: string
  balance?: number
}

/**
 * Same field set as {@link PartyBeneficiaryValue}, but a distinct wire type: it pays
 * into a reOS consolidated wallet rather than an external bank account.
 */
export type ConsolidatedWalletBeneficiaryValue = PartyBeneficiaryValue

export type EasyPayBeneficiaryValue = {
  id?: string
  easyPayReference: string
  beneficiaryTag: string
  amount?: number
  vat?: boolean
  recipientName?: string
}

export type DepositBeneficiaryValue = {
  id?: string
  /** Optional on the wire — a deposit can be held without a resolved party. */
  partyId?: string
  accountId?: string
  reference: string
  beneficiaryTag: string
  amount?: number
  vat?: boolean
  transfer: boolean
}

export type PartyBeneficiary = {
  beneficiary: {
    type: typeof BeneficiaryType.PartyBeneficiary
    value: PartyBeneficiaryValue
  }
}

export type ConsolidatedWalletBeneficiary = {
  beneficiary: {
    type: typeof BeneficiaryType.ConsolidatedWalletBeneficiary
    value: ConsolidatedWalletBeneficiaryValue
  }
}

export type EasyPayBeneficiary = {
  beneficiary: {
    type: typeof BeneficiaryType.EasyPayBeneficiary
    value: EasyPayBeneficiaryValue
  }
}

export type DepositBeneficiary = {
  beneficiary: {
    type: typeof BeneficiaryType.DepositBeneficiary
    value: DepositBeneficiaryValue
  }
}

/**
 * Every beneficiary variant the API can send. Discriminate on `beneficiary.type`.
 */
export type Beneficiary = PartyBeneficiary | ConsolidatedWalletBeneficiary | EasyPayBeneficiary | DepositBeneficiary

/**
 * The union of every `beneficiary.value` shape. Reading a field that only some
 * variants carry (`partyId`, `amount`, `id`) needs a `beneficiary.type` check first.
 */
export type BeneficiaryValue =
  | PartyBeneficiaryValue
  | ConsolidatedWalletBeneficiaryValue
  | EasyPayBeneficiaryValue
  | DepositBeneficiaryValue
